import { Orcamento, PerfilMei, TransacaoPix, OrcamentoStatus, OutboxAction, OutboxItem } from '@/contracts/schemas';
import { generatePixPayload } from './pix';
import { canTransitionStatus } from './orcamento-calc';
import {
  idbGetAllOrcamentos,
  idbSaveOrcamento,
  idbDeleteOrcamento,
  idbGetPerfil,
  idbSavePerfil,
  idbClearAll
} from './idb';

const STORAGE_KEYS = {
  PERFIL: 'zaporca_perfil_mei',
  ORCAMENTOS: 'zaporca_orcamentos',
  OUTBOX: 'zaporca_outbox_queue',
  TRANSACOES: 'zaporca_transacoes_pix',
  THEME: 'brup-theme'
};

const LEGACY_THEME_KEY = 'zaporca_theme';

export type AppTheme = 'light' | 'dark';

const SEED_PERFIL: PerfilMei = {
  id: 'mei-jeremias-01',
  nome: 'Jeremias Cardoso',
  nomeFantasia: 'Jeremias Elétrica & Pintura Residencial',
  documento: '45.123.789/0001-90',
  telefone: '11987654321',
  email: 'codjeremias@gmail.com',
  chavePix: 'codjeremias@gmail.com',
  tipoChavePix: 'email',
  cidade: 'Sao Paulo',
  uf: 'SP',
  ramoAtividade: 'Eletricista e Pintor Residencial'
};

const CLEAN_ANON_PERFIL: PerfilMei = {
  id: 'mei-default',
  nome: 'Minha Empresa MEI',
  nomeFantasia: '',
  documento: '00.000.000/0001-00',
  telefone: '11999999999',
  email: '',
  chavePix: '11999999999',
  tipoChavePix: 'telefone',
  cidade: 'Sao Paulo',
  uf: 'SP',
  ramoAtividade: 'Prestação de Serviços'
};

const SEED_ORCAMENTOS: Orcamento[] = [
  {
    id: 'orc-001',
    uuid: 'a1b2c3d4-e5f6-7890-abcd-111111111111',
    numero: 'ORC-2026-001',
    titulo: 'Reforma Elétrica do Quadro e Instalação de Ar Condicionado',
    dataCriacao: new Date(Date.now() - 3 * 86400000).toISOString(),
    dataValidade: new Date(Date.now() + 7 * 86400000).toISOString(),
    perfilMei: SEED_PERFIL,
    cliente: {
      id: 'cli-001',
      nome: 'Mariana Souza',
      telefone: '11999887766',
      email: 'mariana.souza@email.com',
      endereco: 'Rua das Flores, 120, Apto 42 - Pinheiros',
      cidade: 'São Paulo'
    },
    itens: [
      {
        id: 'item-1',
        descricao: 'Substituição e balanceamento do Quadro de Distribuição (QDF) com 12 disjuntores DIN + DPS + DR',
        quantidade: 1,
        unidade: 'servico',
        valorUnitario: 850.00,
        tipo: 'servico',
        subtotal: 850.00
      },
      {
        id: 'item-2',
        descricao: 'Instalação de circuito dedicado 220V para Ar Condicionado 18.000 BTUs com fiação 4mm',
        quantidade: 2,
        unidade: 'pontos',
        valorUnitario: 350.00,
        tipo: 'servico',
        subtotal: 700.00
      },
      {
        id: 'item-3',
        descricao: 'Quadro Tigre 12/16 disjuntores + Barramento Bifásico pente cobre',
        quantidade: 1,
        unidade: 'un',
        valorUnitario: 250.00,
        tipo: 'material',
        subtotal: 250.00
      }
    ],
    valorSubtotal: 1800.00,
    desconto: 0,
    tipoDesconto: 'fixo',
    valorTotal: 1800.00,
    percentualSinal: 50,
    valorSinal: 900.00,
    valorRestante: 900.00,
    observacoes: 'Garantia de 1 ano na instalação elétrica. Testes de fuga e aterramento inclusos.',
    condicoesPagamento: '50% de sinal via Pix para reserva de data e compra de materiais. Saldo de 50% na conclusão e teste.',
    prazoEstimado: '2 dias úteis',
    status: 'sinal_pago',
    linkPublico: '#/p/a1b2c3d4-e5f6-7890-abcd-111111111111',
    dataPagamentoSinal: new Date(Date.now() - 1 * 86400000).toISOString(),
    historicoEventos: [
      {
        dataHora: new Date(Date.now() - 3 * 86400000).toISOString(),
        tipo: 'criado',
        descricao: 'Orçamento gerado e salvo pelo MEI'
      },
      {
        dataHora: new Date(Date.now() - 2 * 86400000).toISOString(),
        tipo: 'enviado_whatsapp',
        descricao: 'Enviado para o WhatsApp da cliente Mariana'
      },
      {
        dataHora: new Date(Date.now() - 1 * 86400000).toISOString(),
        tipo: 'sinal_pago',
        descricao: 'Prestador marcou que recebeu o sinal de R$ 900,00'
      }
    ]
  },
  {
    id: 'orc-002',
    uuid: 'b2c3d4e5-f6a7-8901-bcde-222222222222',
    numero: 'ORC-2026-002',
    titulo: 'Pintura Completa de Apartamento 65m² com Tinta Suvinil Fosco',
    dataCriacao: new Date(Date.now() - 1 * 86400000).toISOString(),
    dataValidade: new Date(Date.now() + 10 * 86400000).toISOString(),
    perfilMei: SEED_PERFIL,
    cliente: {
      id: 'cli-002',
      nome: 'Carlos Eduardo Lima',
      telefone: '11977665544',
      endereco: 'Av. Paulista, 1500 - Bela Vista',
      cidade: 'São Paulo'
    },
    itens: [
      {
        id: 'item-201',
        descricao: 'Preparação de paredes: lixamento, emassamento de fissuras com massa acrílica e aplicação de fundo preparador',
        quantidade: 1,
        unidade: 'servico',
        valorUnitario: 1200.00,
        tipo: 'servico',
        subtotal: 1200.00
      },
      {
        id: 'item-202',
        descricao: 'Pintura de teto e paredes em 3 demãos com Tinta Suvinil Rende e Cobre',
        quantidade: 1,
        unidade: 'servico',
        valorUnitario: 1800.00,
        tipo: 'servico',
        subtotal: 1800.00
      }
    ],
    valorSubtotal: 3000.00,
    desconto: 200.00,
    tipoDesconto: 'fixo',
    valorTotal: 2800.00,
    percentualSinal: 30,
    valorSinal: 840.00,
    valorRestante: 1960.00,
    observacoes: 'Proteção total de rodapés, tomadas e pisos com plástico bolha e papel crepom inclusa.',
    condicoesPagamento: '30% sinal Pix na assinatura + 70% na entrega das chaves.',
    prazoEstimado: '4 dias úteis',
    status: 'enviado',
    linkPublico: '#/p/b2c3d4e5-f6a7-8901-bcde-222222222222',
    historicoEventos: [
      {
        dataHora: new Date(Date.now() - 1 * 86400000).toISOString(),
        tipo: 'criado',
        descricao: 'Orçamento criado'
      },
      {
        dataHora: new Date(Date.now() - 1 * 86400000).toISOString(),
        tipo: 'enviado_whatsapp',
        descricao: 'Enviado link via WhatsApp'
      }
    ]
  },
  {
    id: 'orc-003',
    uuid: 'c3d4e5f6-a7b8-9012-cdef-333333333333',
    numero: 'ORC-2026-003',
    titulo: 'Instalação de 12 Pontos de LED e Pendentes na Cozinha',
    dataCriacao: new Date().toISOString(),
    dataValidade: new Date(Date.now() + 15 * 86400000).toISOString(),
    perfilMei: SEED_PERFIL,
    cliente: {
      id: 'cli-003',
      nome: 'Ana Beatriz Rocha',
      telefone: '11988776655',
      endereco: 'Rua Harmonia, 450 - Vila Madalena',
      cidade: 'São Paulo'
    },
    itens: [
      {
        id: 'item-301',
        descricao: 'Furação de gesso e instalação de Spots LED embutidos 7W',
        quantidade: 12,
        unidade: 'pontos',
        valorUnitario: 40.00,
        tipo: 'servico',
        subtotal: 480.00
      },
      {
        id: 'item-302',
        descricao: 'Instalação e alinhamento de 2 Pendentes sobre bancada ilha',
        quantidade: 2,
        unidade: 'un',
        valorUnitario: 100.00,
        tipo: 'servico',
        subtotal: 200.00
      }
    ],
    valorSubtotal: 680.00,
    desconto: 0,
    tipoDesconto: 'fixo',
    valorTotal: 680.00,
    percentualSinal: 50,
    valorSinal: 340.00,
    valorRestante: 340.00,
    observacoes: 'Acompanha teste de acendimento com dimmer.',
    condicoesPagamento: '50% sinal Pix no agendamento + 50% na conclusão.',
    prazoEstimado: '1 dia',
    status: 'rascunho',
    linkPublico: '#/p/c3d4e5f6-a7b8-9012-cdef-333333333333',
    historicoEventos: [
      {
        dataHora: new Date().toISOString(),
        tipo: 'criado',
        descricao: 'Rascunho criado'
      }
    ]
  }
];

class StorageRepository {
  private subscribers: Array<() => void> = [];
  private memoryTheme: AppTheme = 'light';
  private memoryOutbox: OutboxItem[] = [];
  private memoryPerfil: PerfilMei = { ...SEED_PERFIL };
  private memoryOrcamentos: Orcamento[] = [];

  constructor() {
    this.memoryOrcamentos = SEED_ORCAMENTOS.map(orc => {
      const pix = generatePixPayload({
        chavePix: orc.perfilMei.chavePix,
        tipoChave: orc.perfilMei.tipoChavePix,
        nomeRecebedor: orc.perfilMei.nome,
        cidadeRecebedor: orc.perfilMei.cidade,
        valor: orc.valorSinal > 0 ? orc.valorSinal : orc.valorTotal,
        txId: orc.numero.replace(/[^a-zA-Z0-9]/g, '')
      });
      return {
        ...orc,
        payloadPix: {
          chavePix: orc.perfilMei.chavePix,
          tipoChave: orc.perfilMei.tipoChavePix,
          nomeRecebedor: orc.perfilMei.nome,
          cidadeRecebedor: orc.perfilMei.cidade,
          valor: orc.valorSinal > 0 ? orc.valorSinal : orc.valorTotal,
          txId: pix.txId,
          payloadFormatado: pix.brCode
        }
      };
    });
    this.initSeedData();
    if (typeof window !== 'undefined') {
      this.initIndexedDb();
      if (window.addEventListener) {
        window.addEventListener('online', () => {
          this.drainOutbox();
        });
      }
    }
  }

  private async initIndexedDb(): Promise<void> {
    try {
      const perfilIDB = await idbGetPerfil();
      const orcsIDB = await idbGetAllOrcamentos();

      let mudou = false;
      if (perfilIDB) {
        this.memoryPerfil = perfilIDB;
        if (this.isLocalStorageAvailable()) {
          localStorage.setItem(STORAGE_KEYS.PERFIL, JSON.stringify(perfilIDB));
        }
        mudou = true;
      } else {
        await idbSavePerfil(this.memoryPerfil);
      }

      if (orcsIDB && orcsIDB.length > 0) {
        this.memoryOrcamentos = orcsIDB;
        if (this.isLocalStorageAvailable()) {
          localStorage.setItem(STORAGE_KEYS.ORCAMENTOS, JSON.stringify(orcsIDB));
        }
        mudou = true;
      } else {
        for (const orc of this.memoryOrcamentos) {
          await idbSaveOrcamento(orc);
        }
      }

      if (mudou) {
        this.notify();
      }
    } catch (e) {
      console.warn('ZapOrca: Sincronização IndexedDB:', e);
    }
  }

  private isLocalStorageAvailable(): boolean {
    try {
      return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
    } catch {
      return false;
    }
  }

  private initSeedData(): void {
    if (!this.isLocalStorageAvailable()) return;

    if (!localStorage.getItem(STORAGE_KEYS.PERFIL)) {
      localStorage.setItem(STORAGE_KEYS.PERFIL, JSON.stringify(SEED_PERFIL));
    }

    if (!localStorage.getItem(STORAGE_KEYS.ORCAMENTOS)) {
      localStorage.setItem(STORAGE_KEYS.ORCAMENTOS, JSON.stringify(this.memoryOrcamentos));
    }
  }

  public subscribe(callback: () => void): () => void {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(s => s !== callback);
    };
  }

  private notify(): void {
    this.subscribers.forEach(cb => {
      try {
        cb();
      } catch (err) {
        console.error('Erro no listener do storage:', err);
      }
    });
  }

  public getPerfilMei(): PerfilMei {
    if (!this.isLocalStorageAvailable()) return this.memoryPerfil;
    const raw = localStorage.getItem(STORAGE_KEYS.PERFIL);
    return raw ? JSON.parse(raw) : this.memoryPerfil;
  }

  public savePerfilMei(perfil: PerfilMei): void {
    this.memoryPerfil = perfil;
    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(STORAGE_KEYS.PERFIL, JSON.stringify(perfil));
    }
    idbSavePerfil(perfil).catch(() => {});
    this.enqueueOutbox('UPSERT_PERFIL', perfil.id, perfil);
    this.notify();
  }

  public getOrcamentos(): Orcamento[] {
    if (!this.isLocalStorageAvailable()) return [...this.memoryOrcamentos];
    const raw = localStorage.getItem(STORAGE_KEYS.ORCAMENTOS);
    return raw ? JSON.parse(raw) : [];
  }

  public getOrcamentoById(id: string): Orcamento | undefined {
    return this.getOrcamentos().find(o => o.id === id);
  }

  public getOrcamentoByUuid(uuid: string): Orcamento | undefined {
    return this.getOrcamentos().find(o => o.uuid === uuid);
  }

  public saveOrcamento(orcamento: Orcamento): Orcamento {
    const list = this.getOrcamentos();
    const index = list.findIndex(o => o.id === orcamento.id || o.uuid === orcamento.uuid);

    const valorCobrar = orcamento.valorSinal > 0 ? orcamento.valorSinal : orcamento.valorTotal;
    const pixResult = generatePixPayload({
      chavePix: orcamento.perfilMei.chavePix,
      tipoChave: orcamento.perfilMei.tipoChavePix,
      nomeRecebedor: orcamento.perfilMei.nome,
      cidadeRecebedor: orcamento.perfilMei.cidade,
      valor: valorCobrar,
      txId: orcamento.numero.replace(/[^a-zA-Z0-9]/g, '')
    });

    const orcamentoAtualizado: Orcamento = {
      ...orcamento,
      payloadPix: {
        chavePix: orcamento.perfilMei.chavePix,
        tipoChave: orcamento.perfilMei.tipoChavePix,
        nomeRecebedor: orcamento.perfilMei.nome,
        cidadeRecebedor: orcamento.perfilMei.cidade,
        valor: valorCobrar,
        txId: pixResult.txId,
        payloadFormatado: pixResult.brCode
      }
    };

    if (index >= 0) {
      list[index] = orcamentoAtualizado;
    } else {
      list.unshift(orcamentoAtualizado);
    }

    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(STORAGE_KEYS.ORCAMENTOS, JSON.stringify(list));
    } else {
      this.memoryOrcamentos = list;
    }
    idbSaveOrcamento(orcamentoAtualizado).catch(() => {});
    this.enqueueOutbox('UPSERT_ORCAMENTO', orcamentoAtualizado.uuid, orcamentoAtualizado);
    this.notify();
    return orcamentoAtualizado;
  }

  public replaceOrcamentos(list: Orcamento[]): void {
    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(STORAGE_KEYS.ORCAMENTOS, JSON.stringify(list));
    } else {
      this.memoryOrcamentos = [...list];
    }
    this.notify();
  }

  public updateStatus(id: string, newStatus: OrcamentoStatus, motivo?: string): Orcamento | undefined {
    const orc = this.getOrcamentoById(id);
    if (!orc) return undefined;

    if (!canTransitionStatus(orc.status, newStatus)) {
      console.warn(`Transição inválida de ${orc.status} para ${newStatus}`);
    }

    const agora = new Date().toISOString();
    const novoEvento = {
      dataHora: agora,
      tipo: newStatus,
      descricao: motivo || `Status alterado para ${newStatus}`
    };

    const atualizado: Orcamento = {
      ...orc,
      status: newStatus,
      historicoEventos: [...(orc.historicoEventos || []), novoEvento]
    };

    if (newStatus === 'aprovado') {
      atualizado.dataAprovacao = agora;
    } else if (newStatus === 'sinal_pago') {
      atualizado.dataPagamentoSinal = agora;
    }

    return this.saveOrcamento(atualizado);
  }

  public simulateWebhookPixPayment(orcamentoId: string): { success: boolean; orcamento?: Orcamento; transacao?: TransacaoPix } {
    const orc = this.getOrcamentoById(orcamentoId);
    if (!orc) return { success: false };

    const valorPago = orc.valorSinal > 0 ? orc.valorSinal : orc.valorTotal;
    const transacao: TransacaoPix = {
      id: `pix-webhook-${Date.now()}`,
      orcamentoId: orc.id,
      valor: valorPago,
      status: 'confirmada',
      dataHora: new Date().toISOString()
    };

    const orcAtualizado = this.updateStatus(
      orc.id,
      'sinal_pago',
      `Prestador marcou que recebeu o sinal de ${valorPago.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}. O app não confirma o banco.`
    );

    this.enqueueOutbox('PIX_PAYMENT_CONFIRMED', orc.uuid, transacao);
    return {
      success: true,
      orcamento: orcAtualizado,
      transacao
    };
  }

  public deleteOrcamento(id: string): boolean {
    const orc = this.getOrcamentos().find(o => o.id === id || o.uuid === id);
    const list = this.getOrcamentos().filter(o => o.id !== id && o.uuid !== id);
    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(STORAGE_KEYS.ORCAMENTOS, JSON.stringify(list));
    } else {
      this.memoryOrcamentos = list;
    }
    idbDeleteOrcamento(id).catch(() => {});
    if (orc) {
      if (orc.id) idbDeleteOrcamento(orc.id).catch(() => {});
      this.enqueueOutbox('DELETE_ORCAMENTO', orc.uuid, { id: orc.id, uuid: orc.uuid });
    }
    this.notify();
    return true;
  }

  public duplicateOrcamento(id: string): Orcamento | undefined {
    const original = this.getOrcamentoById(id) || this.getOrcamentoByUuid(id);
    if (!original) return undefined;

    const count = this.getOrcamentos().length + 1;
    const novoNumero = `ORC-${new Date().getFullYear()}-${String(count).padStart(3, '0')}`;
    const novoUuid = 'orc-' + Math.random().toString(36).substring(2, 10) + '-' + Date.now();
    const novoId = `orc-${Date.now()}`;

    const duplicado: Orcamento = {
      ...original,
      id: novoId,
      uuid: novoUuid,
      numero: novoNumero,
      titulo: `${original.titulo} (Cópia)`,
      dataCriacao: new Date().toISOString(),
      dataValidade: new Date(Date.now() + 10 * 86400000).toISOString(),
      status: 'rascunho',
      linkPublico: `#/p/${novoUuid}`,
      dataPagamentoSinal: undefined,
      dataAprovacao: undefined,
      historicoEventos: [
        {
          dataHora: new Date().toISOString(),
          tipo: 'criado',
          descricao: `Orçamento duplicado a partir de ${original.numero}`
        }
      ]
    };

    return this.saveOrcamento(duplicado);
  }

  public enqueueOutbox(action: OutboxAction, entityId: string, payload: any): OutboxItem {
    const outbox = this.getOutbox();
    const existingIndex = outbox.findIndex(item => item.entityId === entityId && !item.synced);

    const newItem: OutboxItem = {
      id: `outbox-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      entityId,
      action,
      payload,
      timestamp: new Date().toISOString(),
      synced: false,
      retries: 0
    };

    if (existingIndex >= 0) {
      outbox[existingIndex] = newItem;
    } else {
      outbox.push(newItem);
    }

    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(STORAGE_KEYS.OUTBOX, JSON.stringify(outbox));
    } else {
      this.memoryOutbox = outbox;
    }
    return newItem;
  }

  public getOutbox(): OutboxItem[] {
    if (!this.isLocalStorageAvailable()) return [...this.memoryOutbox];
    const raw = localStorage.getItem(STORAGE_KEYS.OUTBOX);
    return raw ? JSON.parse(raw) : [];
  }

  public clearOutbox(): void {
    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(STORAGE_KEYS.OUTBOX, JSON.stringify([]));
    }
    this.memoryOutbox = [];
  }

  public async drainOutbox(
    syncHandler?: (item: OutboxItem) => Promise<boolean>
  ): Promise<{ processed: number; succeeded: number; failed: number }> {
    const outbox = this.getOutbox();
    const pending = outbox.filter(item => !item.synced);

    let succeeded = 0;
    let failed = 0;

    for (const item of pending) {
      try {
        let ok = false;
        if (syncHandler) {
          ok = await syncHandler(item);
        } else {
          ok = true;
        }

        if (ok) {
          item.synced = true;
          succeeded++;
        } else {
          item.retries = (item.retries || 0) + 1;
          failed++;
        }
      } catch (err: any) {
        item.retries = (item.retries || 0) + 1;
        item.lastError = err?.message || 'Erro de sincronização';
        failed++;
      }
    }

    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(STORAGE_KEYS.OUTBOX, JSON.stringify(outbox));
    } else {
      this.memoryOutbox = outbox;
    }

    this.notify();
    return { processed: pending.length, succeeded, failed };
  }

  /**
   * Elimina completamente todos os orçamentos, dados da empresa, chave Pix e fila Outbox
   * em conformidade com o Art. 18, VI da LGPD (direito de eliminação dos dados).
   */
  public purgeAllUserData(): void {
    this.memoryOrcamentos = [];
    this.memoryPerfil = { ...CLEAN_ANON_PERFIL };
    this.memoryOutbox = [];

    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(STORAGE_KEYS.ORCAMENTOS, JSON.stringify([]));
      localStorage.setItem(STORAGE_KEYS.PERFIL, JSON.stringify(this.memoryPerfil));
      localStorage.setItem(STORAGE_KEYS.OUTBOX, JSON.stringify([]));
      localStorage.setItem(STORAGE_KEYS.TRANSACOES, JSON.stringify([]));
    }

    idbClearAll().catch(() => {});
    this.notify();
  }

  public getTheme(): AppTheme {
    if (!this.isLocalStorageAvailable()) return this.memoryTheme;
    const saved = localStorage.getItem(STORAGE_KEYS.THEME) ?? localStorage.getItem(LEGACY_THEME_KEY);
    if (saved === 'dark' || saved === 'graphite-blue') return 'dark';
    if (saved === 'light') return 'light';
    try {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    } catch {
      /* sem matchMedia */
    }
    return 'light';
  }

  public setTheme(theme: AppTheme): void {
    this.memoryTheme = theme;
    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(STORAGE_KEYS.THEME, theme);
    }
    this.notify();
  }
}

export const storage = new StorageRepository();
